import { Component } from '@theme/component';
import {
  fetchConfig,
  preloadImage,
  onAnimationEnd,
  parseIntOrDefault,
  prefersReducedMotion,
  yieldToMainThread,
} from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const VARIANT_UPDATE_ERROR_MESSAGE = 'We could not update that option. Please try again.';
const LINE_ITEM_PROPERTIES_LOAD_TIMEOUT = 20000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart(addToCartButton, image) {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   * @param {{addToCartButton?: HTMLButtonElement, image?: string}} [feedback]
   */
  animateAddToCart = async function (feedback) {
    const addToCartButton = feedback?.addToCartButton ?? this.refs.addToCartButton;
    const image = feedback === undefined ? this.dataset.productVariantMedia : feedback.image;
    if (addToCartButton.isConnected === false) return;
    if (addToCartButton.dataset.puppet === 'true') return;

    if (
      this.dataset.addToCartAnimation === 'true' &&
      !prefersReducedMotion() &&
      !this.closest('.quick-add-modal')
    ) {
      this.#animateFlyToCart(addToCartButton, image);
    }

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity
 * @property {number} generation
 * @property {Record<string, string> | undefined} properties
 * @property {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
 * @property {string | null} intendedVariantId
 * @property {Promise<unknown> | null} pendingVariantChange
 * @property {string | null} variantResolutionUrl
 *
 * @typedef {QueuedAddToCartItem & {variantId: string}} ResolvedQueuedAddToCartItem
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {number} */
  #pendingAddToCartRequests = 0;

  /** @type {boolean} */
  #variantAvailable = true;

  /** @type {boolean} */
  #lineItemPropertiesReady = true;

  /** @type {MutationObserver | undefined} */
  #lineItemPropertiesObserver;

  /** @type {number | undefined} */
  #lineItemPropertiesTimeout;

  /** @type {boolean} */
  #lineItemPropertiesLoadFailed = false;

  /** @type {QueuedAddToCartItem[]} */
  #addToCartQueue = [];

  /** @type {Promise<unknown> | null} */
  #pendingVariantChange = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.product-information, dialog, product-card, .shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    target?.addEventListener('input', this.#syncExternalLineItemProperties, { signal });
    target?.addEventListener('change', this.#syncExternalLineItemProperties, { signal });
    target?.addEventListener('click', this.#syncExternalLineItemProperties, { signal, capture: true });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    this.#variantAvailable = this.dataset.variantAvailable !== 'false';
    this.#syncLineItemPropertiesRequirement(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#lineItemPropertiesObserver?.disconnect();
    this.#clearLineItemPropertiesTimeout();
  }

  /** @param {HTMLElement} source */
  #syncLineItemPropertiesRequirement(source) {
    const shouldWait = source.dataset.waitForLineItemProperties !== undefined;
    this.toggleAttribute('data-wait-for-line-item-properties', shouldWait);
    this.#lineItemPropertiesObserver?.disconnect();
    this.#lineItemPropertiesObserver = undefined;
    this.#clearLineItemPropertiesTimeout();
    this.#clearLineItemPropertiesError();
    this.#lineItemPropertiesReady = !shouldWait;

    if (shouldWait) {
      const root = this.closest('.product-information, dialog, product-card, .shopify-section');
      this.#lineItemPropertiesObserver = new MutationObserver(this.#updateLineItemPropertiesReady);
      this.#lineItemPropertiesObserver.observe(root ?? this, {
        attributes: true,
        attributeFilter: ['data-product-id'],
        childList: true,
        subtree: true,
      });
      this.#updateLineItemPropertiesReady();
      this.#scheduleLineItemPropertiesError();
    }

    this.#setAddToCartPending(0);
    this.#setAcceleratedCheckoutPending(false);
  }

  #updateLineItemPropertiesReady = () => {
    const root = this.closest('.product-information, dialog, product-card, .shopify-section');
    const personalization = /** @type {HTMLElement | null} */ (
      root?.querySelector('[data-sp-personalization-accordion]') ?? null
    );
    const ready = Boolean(
      root?.dataset.productId === this.dataset.productId &&
        personalization?.querySelector('[name^="properties["]')
    );
    if (ready === this.#lineItemPropertiesReady) return;

    this.#lineItemPropertiesReady = ready;
    if (ready) {
      this.#clearLineItemPropertiesTimeout();
      this.#clearLineItemPropertiesError();
    } else {
      this.#scheduleLineItemPropertiesError();
    }
    this.#setAddToCartPending(0);
    this.#setAcceleratedCheckoutPending(false);
  };

  #scheduleLineItemPropertiesError() {
    const errorMessage = this.dataset.lineItemPropertiesError;
    if (
      this.#lineItemPropertiesTimeout !== undefined ||
      this.#lineItemPropertiesReady ||
      this.dataset.waitForLineItemProperties === undefined ||
      !errorMessage
    ) {
      return;
    }

    const expectedGeneration = this.#variantChangeGeneration;
    const expectedProductId = this.dataset.productId;
    const expectedVariantId = this.refs.variantId.value;
    this.#lineItemPropertiesTimeout = setTimeout(() => {
      this.#lineItemPropertiesTimeout = undefined;
      if (
        this.#lineItemPropertiesReady ||
        this.dataset.waitForLineItemProperties === undefined ||
        expectedGeneration !== this.#variantChangeGeneration ||
        expectedProductId !== this.dataset.productId ||
        expectedVariantId !== this.refs.variantId.value
      ) {
        return;
      }

      this.#lineItemPropertiesLoadFailed = true;
      this.#renderAddToCartError(errorMessage);
    }, LINE_ITEM_PROPERTIES_LOAD_TIMEOUT);
  }

  #clearLineItemPropertiesTimeout() {
    if (this.#lineItemPropertiesTimeout === undefined) return;

    clearTimeout(this.#lineItemPropertiesTimeout);
    this.#lineItemPropertiesTimeout = undefined;
  }

  #clearLineItemPropertiesError() {
    if (!this.#lineItemPropertiesLoadFailed) return;

    const errorMessage = this.dataset.lineItemPropertiesError;
    const addToCartTextError = this.refs.addToCartTextError;
    this.#lineItemPropertiesLoadFailed = false;

    if (errorMessage && addToCartTextError?.childNodes[2]?.textContent === errorMessage) {
      addToCartTextError.classList.add('hidden');
    }
    if (errorMessage && this.refs.liveRegion.textContent === errorMessage) this.#clearLiveRegionText();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const item = this.#createQueuedAddToCartItem();
      this.#addToCartQueue.push(item);
      this.#setAddToCartPending(1);
      this.dispatchEvent(
        new CustomEvent('product-form:add-pending', {
          bubbles: true,
          detail: { promise: item.deferredEventPromise.promise },
        })
      );

      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      properties: this.#getLineItemProperties(),
      deferredEventPromise: CartLinesUpdateEvent.createPromise(),
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  /** @param {number} change */
  #setAddToCartPending(change) {
    this.#pendingAddToCartRequests = Math.max(0, this.#pendingAddToCartRequests + change);
    const pending = this.#pendingAddToCartRequests > 0;
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of allAddToCartContainers) {
      const button = container.refs?.addToCartButton ?? container.querySelector('[ref="addToCartButton"]');
      if (!button) continue;

      if (pending) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
      button.disabled = pending || !this.#variantAvailable || !this.#lineItemPropertiesReady;
    }
  }

  /** @param {boolean} pending */
  #setAcceleratedCheckoutPending(pending) {
    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container) return;

    if (pending) {
      container.setAttribute('aria-busy', 'true');
    } else {
      container.removeAttribute('aria-busy');
    }
    container.toggleAttribute('inert', pending || !this.#variantAvailable || !this.#lineItemPropertiesReady);
  }

  /** @param {Error} error */
  #rejectQueuedAddToCart(error) {
    const queuedItems = this.#addToCartQueue;
    this.#addToCartQueue = [];
    for (const item of queuedItems) item.deferredEventPromise.reject(error);
    this.#setAddToCartPending(-queuedItems.length);
  }

  /** @param {Event} [event] */
  #syncExternalLineItemProperties = (event) => {
    const form = this.querySelector('form');
    const root = this.closest('.product-information, dialog, product-card, .shopify-section');
    if (!form || !root) return;

    if (event?.type === 'click' && event.target instanceof HTMLElement && event.target.closest('.so-validation-mask')) {
      form.reportValidity();
    }

    const fields = root.querySelectorAll(
      [
        'input[name^="properties["]:not([data-sp-line-item-property-mirror])',
        'textarea[name^="properties["]:not([data-sp-line-item-property-mirror])',
        'select[name^="properties["]:not([data-sp-line-item-property-mirror])',
      ].join(',')
    );
    const mirrors = new Map(
      Array.from(form.querySelectorAll('input[data-sp-line-item-property-mirror]')).map((mirror) => [
        mirror.name,
        mirror,
      ])
    );
    const activeNames = new Set();

    for (const field of fields) {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLTextAreaElement ||
          field instanceof HTMLSelectElement
        )
      ) {
        continue;
      }

      if (field.form === form || field.disabled || !field.name) continue;

      if (field instanceof HTMLInputElement) {
        if (field.type === 'file') continue;
        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) continue;
      }

      if (!field.value) continue;

      let mirror = mirrors.get(field.name);
      if (!mirror) {
        mirror = document.createElement('input');
        mirror.type = 'hidden';
        mirror.name = field.name;
        mirror.setAttribute('data-sp-line-item-property-mirror', '');
        form.appendChild(mirror);
        mirrors.set(field.name, mirror);
      }

      mirror.value = field.value;
      activeNames.add(field.name);
    }

    for (const [name, mirror] of mirrors) {
      if (!activeNames.has(name)) mirror.remove();
    }
  };

  /** @returns {Record<string, string> | undefined} */
  #getLineItemProperties() {
    const form = this.querySelector('form');
    if (!form) return undefined;

    this.#syncExternalLineItemProperties();

    /** @type {Record<string, string>} */
    const properties = {};
    for (const [name, value] of new FormData(form).entries()) {
      if (typeof value !== 'string' || !name.startsWith('properties[') || !name.endsWith(']') || !value) continue;

      const propertyName = name.slice('properties['.length, -1);
      if (propertyName) properties[propertyName] = value;
    }

    return Object.keys(properties).length ? properties : undefined;
  }

  /** @param {NodeListOf<AddToCartComponent>} containers */
  #captureAddToCartFeedback(containers) {
    return Array.from(containers).map((container) => {
      const addToCartButton = container.refs.addToCartButton;
      return {
        addToCartButton,
        addedText: addToCartButton.querySelector('.add-to-cart-text--added')?.textContent?.trim(),
        container,
        image: container.dataset.productVariantMedia,
      };
    });
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          this.#setAddToCartPending(0);
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    this.#syncExternalLineItemProperties();
    const formData = new FormData(form);
    const productId = this.dataset.productId;
    const addToCartFeedback = this.#captureAddToCartFeedback(allAddToCartContainers);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.#setAddToCartPending(1);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then(async (response) => {
        const responseBody = await response.json();

        if (!response.ok || responseBody.status) {
          const errorMessage = responseBody.message || responseBody.description || 'Add to cart failed';
          this.dispatchEvent(
            new CartErrorEvent({
              error: errorMessage,
              code: 'INVALID',
              detail: {
                description: responseBody.description,
                errors: responseBody.errors,
              },
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          this.#showAddToCartError(errorMessage);
          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          for (const { addToCartButton, container, image } of addToCartFeedback) {
            container.animateAddToCart({ addToCartButton, image });
          }

          // Add aria-live region to inform screen readers that the item was added
          const submittedFeedback = addToCartFeedback[0];
          if (submittedFeedback) {
            const addedText = submittedFeedback.addedText || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId,
                  sections: responseBody.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }
            })
            .catch(deferredEventPromise.reject);
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        const errorMessage = error?.message || 'Network error during add to cart';
        this.#showAddToCartError(errorMessage);

        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#setAddToCartPending(-1);
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {ResolvedQueuedAddToCartItem[]} items
   */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    for (const item of items) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: item.variantId, quantity: item.quantity }],
          promise: item.deferredEventPromise.promise,
        })
      );
    }

    const { addToCartTextError } = this.refs;
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    const addToCartFeedback = this.#captureAddToCartFeedback(allAddToCartContainers);
    const productId = this.dataset.productId;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        ...(item.properties ? { properties: item.properties } : {}),
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        const responseBody = await response.json();

        if (!response.ok || responseBody.status) {
          const errorMessage = responseBody.message || responseBody.description || 'Add to cart failed';
          this.dispatchEvent(
            new CartErrorEvent({
              error: errorMessage,
              code: 'INVALID',
              detail: {
                description: responseBody.description,
                errors: responseBody.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) => {
              const result = {
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount: totalQuantity,
                  productId,
                },
              };
              for (const item of items) item.deferredEventPromise.resolve(result);
            })
            .catch((error) => {
              for (const item of items) item.deferredEventPromise.reject(error);
            });

          this.#showAddToCartError(errorMessage);
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        for (const { addToCartButton, container, image } of addToCartFeedback) {
          container.animateAddToCart({ addToCartButton, image });
        }

        const submittedFeedback = addToCartFeedback[0];
        if (submittedFeedback) {
          const addedText = submittedFeedback.addedText || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        const result = {
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId,
            sections: responseBody.sections,
            didError: false,
          },
        };
        for (const item of items) item.deferredEventPromise.resolve(result);
        this.#updateCartQuantity(cart);
      })
      .catch((error) => {
        console.error(error);
        for (const item of items) item.deferredEventPromise.reject(error);

        const errorMessage = error?.message || 'Network error during add to cart';
        this.#showAddToCartError(errorMessage);

        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#setAddToCartPending(-items.length);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /** @param {*} message */
  #renderAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    this.#setLiveRegionText(message);

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        addToCartTextError.appendChild(document.createTextNode(message));
      }
    }
  }

  /** @param {*} message */
  #showAddToCartError(message) {
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#renderAddToCartError(message);

    this.#timeout = setTimeout(() => {
      this.#timeout = undefined;
      addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#clearLineItemPropertiesTimeout();
    this.#clearLineItemPropertiesError();
    this.refs.addToCartTextError?.classList.add('hidden');
    this.refs.addToCartTextError?.removeAttribute('aria-live');
    this.#clearLiveRegionText();

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    if (!this.#variantChangeInProgress) this.#setAddToCartPending(1);
    this.#setAcceleratedCheckoutPending(true);
    this.#variantChangeInProgress = true;
    this.#pendingVariantChange = event.promise;

    try {
      const { detail } = await event.promise;
      if (generation !== this.#variantChangeGeneration) return;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';
      const selectedVariantId = resource?.id?.toString();
      this.#variantAvailable = Boolean(selectedVariantId && resource?.available !== false);
      const newProductForm = /** @type {HTMLElement | null} */ (html.querySelector('product-form-component'));
      if (newProductForm) this.#syncLineItemPropertiesRequirement(newProductForm);

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);
      this.#setAddToCartPending(0);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } catch (error) {
      if (generation === this.#variantChangeGeneration) {
        const variantError = error instanceof Error ? error : new Error(error?.message || 'Variant update failed');
        variantError.name = error?.name || variantError.name;
        if (variantError.name !== 'AbortError') {
          this.#showAddToCartError(VARIANT_UPDATE_ERROR_MESSAGE);
          this.#scheduleLineItemPropertiesError();
        }
      }
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
        this.#setAddToCartPending(-1);
        this.#setAcceleratedCheckoutPending(false);

        await this.#drainAddToCartQueue();
        if (generation === this.#variantChangeGeneration) this.#pendingVariantChange = null;
      }
    }
  };

  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];
    /** @type {ResolvedQueuedAddToCartItem[]} */
    const resolvedItems = [];

    for (const item of queuedItems) {
      try {
        const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
        if (resolvedItem) {
          resolvedItems.push(resolvedItem);
        } else {
          throw new Error('Selected variant is unavailable');
        }
      } catch (error) {
        item.deferredEventPromise.reject(error);
        this.#showAddToCartError(error?.message || 'Unable to add this item. Please try again.');
        this.#setAddToCartPending(-1);
      }
    }

    this.#processBatchAddToCart(resolvedItems);
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<ResolvedQueuedAddToCartItem | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints, error, requiresLineItemProperties } = await this.#resolveQueuedVariant(item);
    if (requiresLineItemProperties) {
      throw new Error('Complete the personalization fields before adding this item.');
    }

    if (!variantId) {
      if (error) throw error;
      return null;
    }

    const quantity = this.#normalizeQueuedQuantity(item.quantity, quantityConstraints);
    if (quantity === null) return null;

    return {
      ...item,
      variantId,
      quantity,
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   */
  async #resolveQueuedVariant(item) {
    let resolvedVariantId = null;
    let available;
    let quantityConstraints = null;
    let resolutionError;
    let requiresLineItemProperties = false;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await item.pendingVariantChange
        );
        const resource = result?.detail?.resource;
        const html = result?.detail?.html;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
        requiresLineItemProperties =
          html?.querySelector?.('product-form-component')?.dataset.waitForLineItemProperties !== undefined;
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch (error) {
        resolutionError = error;
        const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
          requiresLineItemProperties: false,
        }));
        resolvedVariantId = resolvedVariant.variantId;
        available = resolvedVariant.available;
        quantityConstraints = resolvedVariant.quantityConstraints;
        requiresLineItemProperties = resolvedVariant.requiresLineItemProperties;
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return {
      variantId,
      quantityConstraints,
      error: variantId ? undefined : resolutionError,
      requiresLineItemProperties,
    };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number | null}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : max - cartQuantity;
    if (effectiveMax !== null && effectiveMax <= 0) return null;

    const effectiveMin = effectiveMax === null ? min : Math.min(min, effectiveMax);

    let normalizedQuantity = quantity;
    if ((quantity - min) % step !== 0) {
      normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
    }

    return Math.max(effectiveMin, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
  }

  /** @param {Document | Element | null | undefined} html */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /** @param {string | null} variantResolutionUrl */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) {
      return {
        variantId: null,
        available: undefined,
        quantityConstraints: null,
        requiresLineItemProperties: false,
      };
    }

    const response = await fetch(variantResolutionUrl, { credentials: 'same-origin' });
    if (!response.ok) {
      return {
        variantId: null,
        available: false,
        quantityConstraints: null,
        requiresLineItemProperties: false,
      };
    }

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const requiresLineItemProperties =
      html.querySelector('product-form-component')?.dataset.waitForLineItemProperties !== undefined;
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) {
      return { variantId: null, available: false, quantityConstraints, requiresLineItemProperties };
    }

    const resource = JSON.parse(textContent);
    if (!resource || typeof resource !== 'object') {
      return { variantId: null, available: false, quantityConstraints, requiresLineItemProperties };
    }

    return {
      variantId: resource.id != null ? String(resource.id) : null,
      available: resource.available !== false,
      quantityConstraints,
      requiresLineItemProperties,
    };
  }

  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? []);
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
